/**
 * JTNC QFAA trade P&L straight from Tradier history: option fills and
 * expirations on the tracked underlyings, already net of per-contract
 * commissions. The admin Platform log card and the admin Performance chart
 * both read these numbers so they cannot drift apart.
 *
 * Echo forwards orders into some of the same Tradier accounts and Tradier
 * history carries no order tag, so each option contract is attributed by its
 * opening DTE (desk rule):
 *   - jtnc:  first fill on the expiration date (0DTE)
 *   - echo:  first fill one trading session before expiration (1DTE), however
 *            the position was closed
 *   - other: anything else (opened earlier, non-option fills, or a contract
 *            whose opening fill is outside the loaded history)
 * Up to the desk's reconciliation cutoff the desk's approved fill list takes
 * precedence over the rule (it also drops personal and test trades).
 */
import { underlyingOf } from './adminTradeSymbols.js';

const round2 = (n) => Math.round(n * 100) / 100;

// NYSE full-day closures.
const MARKET_HOLIDAYS = new Set([
  '2025-01-01', '2025-01-09', '2025-01-20', '2025-02-17', '2025-04-18', '2025-05-26',
  '2025-06-19', '2025-07-04', '2025-09-01', '2025-11-27', '2025-12-25',
  '2026-01-01', '2026-01-19', '2026-02-16', '2026-04-03', '2026-05-25', '2026-06-19',
  '2026-07-03', '2026-09-07', '2026-11-26', '2026-12-25',
  '2027-01-01', '2027-01-18', '2027-02-15', '2027-03-26', '2027-05-31', '2027-06-18',
  '2027-07-05', '2027-09-06', '2027-11-25', '2027-12-24',
]);

const isTradingDay = (iso) => {
  const day = new Date(`${iso}T12:00:00Z`).getUTCDay();
  return day !== 0 && day !== 6 && !MARKET_HOLIDAYS.has(iso);
};

export function previousTradingDay(iso) {
  const d = new Date(`${iso}T12:00:00Z`);
  do {
    d.setUTCDate(d.getUTCDate() - 1);
  } while (!isTradingDay(d.toISOString().slice(0, 10)));
  return d.toISOString().slice(0, 10);
}

/** 'SPY261009C00754000' → '2026-10-09'; null for anything that isn't an OCC option symbol. */
export function occExpiration(symbol) {
  const m = /(\d{2})(\d{2})(\d{2})[CP]\d{8}$/.exec(symbol || '');
  return m ? `20${m[1]}-${m[2]}-${m[3]}` : null;
}

const isFillOrExpiry = (e) => e.category === 'trade' || e.category === 'expiration';
/** JTNC credits/bonuses/refunds and Pro / Pro Plus subscription fees are part of JTNC Trade P&L. */
const isAccountItem = (e) => e.category === 'credit' || e.category === 'subscription';

/** Map of option symbol → 'jtnc' | 'echo' | 'other'. */
export function classifyQfaaContracts(events) {
  const opened = new Map();
  for (const e of events || []) {
    if (e.category !== 'trade' || !occExpiration(e.symbol)) continue;
    const first = opened.get(e.symbol);
    if (!first || e.date < first) opened.set(e.symbol, e.date);
  }
  const strategy = new Map();
  for (const [symbol, openDate] of opened) {
    const expiry = occExpiration(symbol);
    if (openDate === expiry) strategy.set(symbol, 'jtnc');
    else if (openDate === previousTradingDay(expiry)) strategy.set(symbol, 'echo');
    else strategy.set(symbol, 'other');
  }
  return strategy;
}

const EXCLUSION_REASONS = ['echo', 'test', 'not_in_master', 'other'];

const masterKey = (date, symbol, amount) => `${date}|${symbol}|${round2(Number(amount) || 0)}`;

/**
 * Splits Tradier fills and expirations into JTNC client trades and everything
 * that must stay out of JTNC Trade P&L, then adds technology reimbursements.
 *
 * - On or before `master.cutoff` the desk's approved list decides: a fill
 *   counts only when it matches a JTNC row of the list (date, contract, cash).
 * - After the cutoff, or for a client with no list, the opening-DTE rule
 *   decides, and contracts traded by an order flagged as a test are dropped.
 *
 * @param events  normalized Tradier history events
 * @param master  { cutoff: 'YYYY-MM-DD', fills: [{ d, s, a, k: 'jtnc'|'echo' }] } or null
 * @param testSymbols  Set of option symbols from orders flagged test_trade
 * @param reimbursements  [{ date: 'YYYY-MM-DD', amount }]
 */
export function jtncTradeLedger(events, { master = null, testSymbols = null, reimbursements = [] } = {}) {
  const fills = (events || []).filter(isFillOrExpiry);
  const contracts = classifyQfaaContracts(fills);
  const cutoff = master?.cutoff || null;
  const approved = new Map();
  for (const f of master?.fills || []) {
    const key = masterKey(f.d, f.s, f.a);
    if (!approved.has(key)) approved.set(key, []);
    approved.get(key).push(f.k);
  }

  const included = [];
  const excluded = [];
  for (const e of fills) {
    let reason = null;
    if (cutoff && e.date <= cutoff) {
      const strategy = approved.get(masterKey(e.date, e.symbol, e.amount))?.shift();
      if (strategy === 'echo') reason = 'echo';
      else if (strategy !== 'jtnc') {
        // A worthless expiry moves no cash; only list it when the desk's list has the contract's fills.
        if (e.category === 'expiration' && !e.amount) continue;
        reason = 'not_in_master';
      }
    } else {
      const strategy = contracts.get(e.symbol) || 'other';
      if (strategy !== 'jtnc') reason = strategy;
      else if (testSymbols?.has(e.symbol)) reason = 'test';
    }
    if (reason) excluded.push({ ...e, reason });
    else included.push(e);
  }

  const credits = (reimbursements || [])
    .filter((r) => r?.date && Number.isFinite(Number(r.amount)))
    .map((r) => ({ date: r.date, amount: Number(r.amount) }));
  const accountItems = (events || []).filter(isAccountItem);

  const excludedByReason = Object.fromEntries(EXCLUSION_REASONS.map((r) => [r, 0]));
  for (const e of excluded) excludedByReason[e.reason] += e.amount;

  const sum = (rows) => round2(rows.reduce((s, e) => s + e.amount, 0));
  const tradePnl = sum(included);
  const reimbursementPnl = sum(credits);
  const creditPnl = sum(accountItems.filter((e) => e.category === 'credit'));
  const subscriptionPnl = sum(accountItems.filter((e) => e.category === 'subscription'));
  return {
    included,
    excluded,
    reimbursements: credits,
    accountItems,
    tradePnl,
    reimbursementPnl,
    creditPnl,
    subscriptionPnl,
    total: round2(tradePnl + reimbursementPnl + creditPnl + subscriptionPnl),
    excludedByReason: Object.fromEntries(Object.entries(excludedByReason).map(([k, v]) => [k, round2(v)])),
  };
}

/** [{ date: 'YYYY-MM-DD', pnl, bySymbol: { SPY, QQQ, SPX } }] in date order; reimbursements, credits and subscriptions add to pnl only. */
export function jtncDailyPnl(ledger) {
  const days = new Map();
  const day = (date) => {
    if (!days.has(date)) days.set(date, { date, pnl: 0, bySymbol: {} });
    return days.get(date);
  };
  for (const e of ledger?.included || []) {
    const d = day(e.date);
    const root = underlyingOf(e.underlying || e.symbol);
    const sym = root === 'SPXW' ? 'SPX' : root;
    d.pnl += e.amount;
    d.bySymbol[sym] = (d.bySymbol[sym] || 0) + e.amount;
  }
  for (const r of ledger?.reimbursements || []) day(r.date).pnl += r.amount;
  for (const e of ledger?.accountItems || []) day(e.date).pnl += e.amount;
  return [...days.values()]
    .sort((a, b) => (a.date < b.date ? -1 : 1))
    .map((d) => ({
      ...d,
      pnl: round2(d.pnl),
      bySymbol: Object.fromEntries(Object.entries(d.bySymbol).map(([k, v]) => [k, round2(v)])),
    }));
}

/** { 'YYYY-MM': { pnl, trades, reimbursements, credits, subscriptions, excluded } }; pnl is the month's JTNC Trade P&L. */
export function jtncMonthlyPnl(ledger) {
  const months = {};
  const month = (date) => {
    const key = date.slice(0, 7);
    if (!months[key]) months[key] = { pnl: 0, trades: 0, reimbursements: 0, credits: 0, subscriptions: 0, excluded: 0 };
    return months[key];
  };
  for (const e of ledger?.included || []) month(e.date).trades += e.amount;
  for (const e of ledger?.excluded || []) month(e.date).excluded += e.amount;
  for (const r of ledger?.reimbursements || []) month(r.date).reimbursements += r.amount;
  for (const e of ledger?.accountItems || []) {
    month(e.date)[e.category === 'credit' ? 'credits' : 'subscriptions'] += e.amount;
  }
  for (const m of Object.values(months)) {
    for (const k of Object.keys(m)) m[k] = round2(m[k]);
    m.pnl = round2(m.trades + m.reimbursements + m.credits + m.subscriptions);
  }
  return months;
}
